/**
 * Catalogue public (docs/11 §6 et §7).
 *
 * Les filtres vivent dans l'URL plutôt que dans un état local : une recherche
 * partagée ou rechargée doit rendre la même page, et un visiteur qui revient
 * d'une fiche retrouve sa liste telle qu'il l'avait laissée.
 */
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useCourseCatalog,
  useCourseDomains,
  useCourseSuggestions,
} from "@/features/catalog/api/useCatalog";
import { CourseCard } from "@/features/catalog/components/CourseCard";
import { CourseSuggestions } from "@/features/catalog/components/CourseSuggestions";

import { useDebouncedValue } from "@/shared/lib/useDebouncedValue";

import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { LoadMore } from "@/shared/ui/LoadMore";
import { Skeleton } from "@/shared/ui/Skeleton";

const LEVELS = ["beginner", "intermediate", "advanced"] as const;

const FIELD_CLASSES =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm " +
  "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

export function CatalogPage() {
  const { t, i18n } = useTranslation("catalog");
  const navigate = useNavigate();
  const search = useSearch({ from: "/public/courses" });
  const domains = useCourseDomains();
  // Deux états pour un champ : `typed` suit la frappe et alimente
  // l'autocomplétion, `search.q` porte la recherche **validée** et vit dans
  // l'URL. Les confondre ferait relancer le catalogue entier à chaque lettre,
  // et écrirait dans l'historique du navigateur une entrée par caractère.
  const [typed, setTyped] = useState(search.q ?? "");
  const suggestions = useCourseSuggestions(useDebouncedValue(typed));
  const courses = useCourseCatalog({
    ...(search.q !== undefined ? { q: search.q } : {}),
    ...(search.domain !== undefined ? { domain_id: search.domain } : {}),
    ...(search.level !== undefined ? { level: search.level } : {}),
  });

  // Les pages chargées sont aplaties : le curseur ne sert qu'à aller plus loin,
  // et l'écran affiche la liste continue, non une page isolée.
  const rows = courses.data?.pages.flatMap((page) => page.data);
  const total = courses.data?.pages[0]?.meta.total ?? null;

  const update = (patch: Record<string, string | undefined>) => {
    void navigate({
      to: "/courses",
      search: (previous) => {
        const next = { ...previous, ...patch };
        // Une valeur vide est retirée de l'URL : `?level=` n'a pas de sens et
        // rendrait le lien plus difficile à relire.
        return Object.fromEntries(Object.entries(next).filter(([, value]) => Boolean(value)));
      },
    });
  };

  const domainName = (domainId: string): string => {
    const domain = domains.data?.find((item) => item.id === domainId);
    // Repli sur le français puis sur le slug : un domaine ajouté par un
    // administrateur peut n'avoir aucun libellé dans la langue courante, et
    // afficher une case vide serait pire qu'afficher autre chose.
    return domain?.name_i18n[i18n.language] ?? domain?.name_i18n["fr"] ?? domain?.slug ?? "";
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("catalog.title")}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">{t("catalog.subtitle")}</p>
      </header>

      <Card>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="catalog-search">
              {t("catalog.searchLabel")}
            </label>
            <input
              id="catalog-search"
              type="search"
              className={FIELD_CLASSES}
              value={typed}
              placeholder={t("catalog.searchPlaceholder")}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  update({ q: event.currentTarget.value });
                }
              }}
            />
            {/* Les propositions disparaissent dès que la recherche est
                validée : elles servent à trouver le titre, pas à commenter une
                liste de résultats déjà affichée. */}
            {typed !== (search.q ?? "") && (
              <CourseSuggestions suggestions={suggestions.data ?? []} />
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="catalog-domain">
              {t("catalog.domain")}
            </label>
            <select
              id="catalog-domain"
              className={FIELD_CLASSES}
              value={search.domain ?? ""}
              onChange={(event) => {
                update({ domain: event.target.value });
              }}
            >
              <option value="">{t("catalog.allDomains")}</option>
              {domains.data?.map((domain) => (
                <option key={domain.id} value={domain.id}>
                  {domainName(domain.id)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="catalog-level">
              {t("catalog.level")}
            </label>
            <select
              id="catalog-level"
              className={FIELD_CLASSES}
              value={search.level ?? ""}
              onChange={(event) => {
                update({ level: event.target.value });
              }}
            >
              <option value="">{t("catalog.allLevels")}</option>
              {LEVELS.map((level) => (
                <option key={level} value={level}>
                  {t(`levels.${level}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {courses.isPending && <Skeleton className="h-40 w-full" label={t("catalog.title")} />}
      {courses.isError && <ErrorState error={courses.error} />}

      {rows !== undefined && rows.length === 0 && (
        <Card>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("catalog.empty")}</p>
        </Card>
      )}

      {rows !== undefined && rows.length > 0 && (
        <>
          {/* Le total est affiché dès qu'il est connu : une recherche par
              pertinence ne rend qu'une page, et sans ce chiffre le lecteur
              croirait avoir vu tous les résultats. */}
          {search.q !== undefined && total !== null && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t("catalog.rankedResults", { shown: rows.length, total })}
            </p>
          )}
          {/* Nommée : la page porte plusieurs listes (les étiquettes de
              chaque carte en sont), et un lecteur d'écran doit pouvoir dire
              laquelle porte les résultats. */}
          <ul aria-label={t("catalog.results")} className="grid gap-4 sm:grid-cols-2">
            {rows.map((course) => (
              <li key={course.id}>
                <Link to="/courses/$slug" params={{ slug: course.slug }} className="block">
                  <CourseCard course={course} domainName={domainName(course.domain_id)} />
                </Link>
              </li>
            ))}
          </ul>
          <LoadMore
            shown={rows.length}
            hasMore={courses.hasNextPage}
            isLoading={courses.isFetchingNextPage}
            onLoadMore={() => {
              void courses.fetchNextPage();
            }}
          />
        </>
      )}
    </div>
  );
}
